import React, { useState } from "react";
import { styled, alpha } from "@mui/material/styles";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  InputBase,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Badge,
  ListItemButton,
  Typography,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PersonIcon from "@mui/icons-material/Person";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import SchoolIcon from "@mui/icons-material/School";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { Link, useLocation } from "react-router-dom";

const StyledHeader = styled(Box)(({ theme }) => ({
  borderBottom: "1px solid rgba(231, 231, 231, 0.3)",
  backdropFilter: "blur(8px)",
  transition: "all 0.3s ease",
  padding: theme.spacing(1, 2),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  width: "100%",
  position: "sticky",
  top: 0,
  zIndex: 1100,
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(1, 3),
  },
}));

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: "28px",
  backgroundColor: alpha("#eee", 0.2),
  backdropFilter: "blur(8px)",
  border: "1px solid",
  borderColor: alpha("#333", 0.3),
  "&:hover": {
    backgroundColor: alpha("#ddd", 0.3),
    borderColor: alpha("#222", 0.4),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: "100%",
  maxWidth: "500px",
  transition: "all 0.3s ease",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(3),
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: alpha("#000000", 0.7),
  right: 0,
  top: 0,
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "#000000",
  width: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 4, 1, 2),
    width: "100%",
    transition: "all 0.3s ease",
    "&::placeholder": {
      color: alpha("#000000", 0.7),
      opacity: 0.8,
    },
  },
}));

const MenuIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: alpha("#FFFFFF", 0.2),
  backdropFilter: "blur(8px)",
  border: "1px solid",
  borderColor: alpha("#FFFFFF", 0.3),
  padding: "8px",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: alpha("#FFFFFF", 0.3),
    borderColor: alpha("#FFFFFF", 0.4),
    transform: "scale(1.05)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "6px",
  },
}));

const NotificationIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: alpha("#30B0C7", 0.9),
  width: { xs: "32px", sm: "40px" },
  height: { xs: "32px", sm: "40px" },
  transition: "all 0.3s ease",
  backdropFilter: "blur(8px)",
  "&:hover": {
    backgroundColor: "#2799AD",
    transform: "scale(1.1)",
  },
  "& .MuiSvgIcon-root": {
    fontSize: { xs: "18px", sm: "20px" },
    color: "white",
  },
}));

const UserIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: alpha("#444", 0.2),
  backdropFilter: "blur(8px)",
  border: "1px solid",
  borderColor: alpha("#FFFFFF", 0.3),
  transition: "all 0.3s ease",
  width: { xs: "32px", sm: "40px" },
  height: { xs: "32px", sm: "40px" },
  "&:hover": {
    backgroundColor: alpha("#444", 0.3),
    borderColor: alpha("#FFFFFF", 0.4),
    transform: "scale(1.05)",
  },
  "& .MuiSvgIcon-root": {
    fontSize: { xs: "18px", sm: "20px" },
    color: alpha("#000000", 0.7),
  },
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: "10px",
  margin: "4px 8px",
  padding: "10px 16px",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: alpha("#FFFFFF", 0.2),
    transform: "translateX(5px)",
  },
  "&.Mui-selected": {
    backgroundColor: alpha("#FFFFFF", 0.3),
    "&:hover": {
      backgroundColor: alpha("#FFFFFF", 0.35),
    },
  },
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    backgroundColor: "transparent",
    boxShadow: "none",
    borderRight: "none",
  },
  "& .MuiBackdrop-root": {
    backgroundColor: alpha("#000000", 0.3),
  },
}));

const DrawerContent = styled(Box)(({ theme }) => ({
  width: 280,
  height: "100%",
  backgroundColor: alpha("#FFFFFF", 0.1),
  backdropFilter: "blur(16px)",
  borderRight: `1px solid ${alpha("#FFFFFF", 0.2)}`,
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(2),
}));

const UserDrawerContent = styled(Box)(({ theme }) => ({
  width: 300,
  backgroundColor: alpha("#FFFFFF", 0.1),
  backdropFilter: "blur(16px)",
  borderLeft: `1px solid ${alpha("#FFFFFF", 0.2)}`,
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(2),
}));

const NotificationDrawerContent = styled(Box)(({ theme }) => ({
  width: 320,
  backgroundColor: alpha("#FFFFFF", 0.1),
  backdropFilter: "blur(16px)",
  borderLeft: `1px solid ${alpha("#FFFFFF", 0.2)}`,
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(2),
}));

const NotificationItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: alpha("#FFFFFF", 0.1),
  marginBottom: theme.spacing(1),
  transition: "all 0.3s ease",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: alpha("#FFFFFF", 0.2),
    transform: "translateX(5px)",
  },
}));

const UserMenuItem = styled(ListItemButton)(({ theme }) => ({
  borderRadius: "10px",
  margin: "4px 0",
  padding: "12px 16px",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: alpha("#FFFFFF", 0.2),
    transform: "translateX(5px)",
  },
  "&.Mui-selected": {
    backgroundColor: alpha("#FFFFFF", 0.3),
    "&:hover": {
      backgroundColor: alpha("#FFFFFF", 0.35),
    },
  },
}));

const DashboardHeader = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);
  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const lastSegment = pathSegments[pathSegments.length - 1] || "home";
  const navigate = useNavigate();
  const { logout } = useAuth();

  const paths = {
    home: "Home",
    dashboard: "Dashboard",
    courses: "Courses",
    intern: "Careers",
    contactus: "Contact Us",
    aboutus: "About",
    // iiithprep: "IIITH_prep",
  };

  const notifications = [
    {
      id: 1,
      title: "New Course Available",
      message: "Advanced React Course is now available for enrollment",
      time: "2 hours ago",
    },
    {
      id: 2,
      title: "Assignment Due",
      message: "Your JavaScript Fundamentals assignment is due tomorrow",
      time: "5 hours ago",
    },
    {
      id: 3,
      title: "Course Update",
      message: "New material added to your enrolled Python course",
      time: "1 day ago",
    },
  ];
  const logoutUser = () => {
    console.log("hiii");
    logout(); // This will clear localStorage and set user to null
    navigate("/home");
    handleUserDrawerToggle();
  };
  const handleDrawerToggle = () => setDrawerOpen(!drawerOpen);
  const handleUserDrawerToggle = () => setUserDrawerOpen(!userDrawerOpen);
  const handleNotificationDrawerToggle = () =>
    setNotificationDrawerOpen(!notificationDrawerOpen);

  const drawer = (
    <DrawerContent>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 3,
          pl: 1,
        }}
      >
        <img
          src="/logo.png"
          alt="Logo"
          style={{
            width: "33px",
            height: "33px",
            objectFit: "contain",
          }}
        />
      </Box>
      <List sx={{ width: "100%" }}>
        {Object.entries(paths).map(([key, value]) => (
          <ListItem key={key} disablePadding>
            <StyledListItemButton
              component={Link}
              to={`/${key}`}
              selected={lastSegment === key}
              onClick={handleDrawerToggle}
            >
              <ListItemText
                primary={value}
                sx={{
                  "& .MuiListItemText-primary": {
                    fontWeight: lastSegment === key ? 600 : 400,
                    fontSize: "0.95rem",
                    color: alpha("#000000", 0.87),
                  },
                }}
              />
            </StyledListItemButton>
          </ListItem>
        ))}
      </List>
    </DrawerContent>
  );

  const userDrawer = (
    <UserDrawerContent>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: alpha("#000000", 0.87) }}
        >
          User Profile
        </Typography>
      </Box>
      <List>
        {/* <ListItem disablePadding>
          <UserMenuItem
            component={Link}
            to="/dashboard"
            selected={lastSegment === "dashboard"}
            onClick={handleUserDrawerToggle}
          >
            <SchoolIcon sx={{ mr: 2, color: alpha("#000000", 0.87) }} />
            <ListItemText
              primary="Enrolled Courses"
              sx={{
                "& .MuiListItemText-primary": {
                  fontWeight: lastSegment === "dashboard" ? 600 : 400,
                  color: alpha("#000000", 0.87),
                },
              }}
            />
          </UserMenuItem>
        </ListItem> */}
        <ListItem disablePadding>
          <UserMenuItem
            component={Link}
            to="/dashboard"
            selected={lastSegment === "admission"}
            onClick={handleUserDrawerToggle}
          >
            <PersonOutlineIcon sx={{ mr: 2, color: alpha("#000000", 0.87) }} />
            <ListItemText
              primary="Student Info"
              sx={{
                "& .MuiListItemText-primary": {
                  fontWeight: lastSegment === "admission" ? 600 : 400,
                  color: alpha("#000000", 0.87),
                },
              }}
            />
          </UserMenuItem>
        </ListItem>
        <Divider sx={{ my: 2, borderColor: alpha("#000000", 0.1) }} />
        <ListItem disablePadding>
          <UserMenuItem onClick={logoutUser}>
            <LogoutIcon sx={{ mr: 2, color: alpha("#000000", 0.87) }} />
            <ListItemText
              primary="Logout"
              sx={{
                "& .MuiListItemText-primary": {
                  color: alpha("#000000", 0.87),
                },
              }}
            />
          </UserMenuItem>
        </ListItem>
      </List>
    </UserDrawerContent>
  );

  const notificationDrawer = (
    <NotificationDrawerContent>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: alpha("#000000", 0.87) }}
        >
          Notifications
        </Typography>
      </Box>
      {notifications.map((notification) => (
        <NotificationItem key={notification.id}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: alpha("#000000", 0.87) }}
          >
            {notification.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: alpha("#000000", 0.6), my: 1 }}
          >
            {notification.message}
          </Typography>
          <Typography variant="caption" sx={{ color: alpha("#000000", 0.5) }}>
            {notification.time}
          </Typography>
        </NotificationItem>
      ))}
    </NotificationDrawerContent>
  );

  return (
    <>
      <StyledHeader>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 2 },
          }}
        >
          <MenuIconButton
            onClick={handleDrawerToggle}
            aria-label="open menu"
            sx={{
              width: { xs: "32px", sm: "40px" },
              height: { xs: "32px", sm: "40px" },
            }}
          >
            <MenuIcon
              sx={{
                fontSize: { xs: "20px", sm: "24px" },
                color: alpha("#000000", 0.87),
              }}
            />
          </MenuIconButton>
          <Box
            component={Link}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              transition: "transform 0.3s ease",
              "&:hover": {
                transform: "scale(1.05)",
              },
            }}
          >
            <img
              src="/logo.png"
              alt="Logo"
              style={{
                width: "33px",
                height: "33px",
                objectFit: "contain",
              }}
            />
          </Box>
        </Box>

        {/*<Search>
          <StyledInputBase
            placeholder="Search courses, internships..."
            inputProps={{ 'aria-label': 'search' }}
          />
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
        </Search> */}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 2 },
          }}
        >
          {/* <NotificationIconButton onClick={handleNotificationDrawerToggle}>
            <Badge
              badgeContent={3}
              color="error"
              sx={{
                "& .MuiBadge-badge": {
                  backgroundColor: "#FF3B30",
                  minWidth: "18px",
                  height: "18px",
                  padding: "0 4px",
                },
              }}
            >
              <NotificationsIcon />
            </Badge>
          </NotificationIconButton> */}
          <Box
            onClick={() =>
              navigate("/initial-info", {
                state: { fromYapassioButton: true },
              })
            }
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              borderRadius: "10px",
              cursor: "pointer",
              px: { xs: 0.2, sm: 3 },

              minHeight: { xs: "25px", sm: "52px" }, // 👈 mobile tap target
              transition: "all 0.2s ease",
            }}
          >
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 600,
                fontSize: { xs: "0px", sm: "16px" },
                py: { xs: "0px", sm: "6px" },
                px: { xs: "0px", sm: "3px" },
                borderRadius: "8px",
                textAlign: "center",
                whiteSpace: "nowrap",
                backgroundColor: "#30B0C7",
                "&:hover": {
                  backgroundColor: "#279DB2",
                },
              }}
            >
              Go to Counselling
            </Typography>
            <UserIconButton style={{ border: "none", backgroundColor: "#fff" }}>
              <img
                src="/AI_Addon/counselling-header-icon.png"
                style={{ height: "32px", backgroundColor: "#fff" }}
              />
            </UserIconButton>
          </Box>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
            }}
            onClick={() =>
              navigate("/AI/dashboard", {
                state: { fromYapassioButton: true }, // 👈 Add this
              })
            }
          >
            <span
              // style={{
              //   fontSize: "clamp(12px, 2vw, 14px)", // min 10px, fluid in between, max 14px
              //   fontWeight: 800,
              //   color: "#000",
              // }}
              className="yapassio-text"
            >
              Try Yapassio AI
            </span>

            <UserIconButton style={{ border: "none", backgroundColor: "#fff" }}>
              <img
                src="/AI_Addon/ai-header-icon.png"
                style={{ height: "32px", backgroundColor: "#fff" }}
              />
            </UserIconButton>
          </div>

          {/* <UserIconButton
            style={{ borderStyle: "none", backgroundColor: "#fff" }}
            onClick={() => navigate("/AI/dashboard")}
          >
            <img
              src="/AI_Addon/ai-header-icon.png"
              style={{
                height: "35px",
                backgroundColor: "#fff",
                borderStyle: "none",
              }}
            />
          </UserIconButton> */}
          <UserIconButton onClick={handleUserDrawerToggle}>
            <PersonIcon />
          </UserIconButton>
        </Box>
      </StyledHeader>

      <StyledDrawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {drawer}
      </StyledDrawer>

      <StyledDrawer
        anchor="right"
        open={userDrawerOpen}
        onClose={handleUserDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {userDrawer}
      </StyledDrawer>

      <StyledDrawer
        anchor="right"
        open={notificationDrawerOpen}
        onClose={handleNotificationDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {notificationDrawer}
      </StyledDrawer>
    </>
  );
};

export default DashboardHeader;
